import { defineStore } from 'pinia';
import { CHANNEL, SUBSCRIPTION } from '@apps-once/definitions';
import { useOnceStore } from '@apps-once/store';
import { useCartStore } from '@apps-once/store/cart';
import { useAddOnStore } from '@apps-once/store/add-on';
import { usePlansStore } from '@apps-billing-shared/store/plans';

export enum CheckoutSteps {
  SELECT_PLAN_DETAILS = 0,
  ADD_CHECKOUT_DETAILS = 1,
  SELECT_ADD_ON_DETAILS = 2,
}

export type SubscriptionPlan = number;

type CheckoutStoreState = {
  checkoutStep: number;
  isAioBundle: boolean;
};

export const useCheckoutStore = defineStore('once:checkout', {
  state(): CheckoutStoreState {
    return {
      checkoutStep: CheckoutSteps.SELECT_PLAN_DETAILS,
      isAioBundle: false,
    };
  },
  getters: {
    currentCheckoutStep(state: CheckoutStoreState): number {
      return state.checkoutStep;
    },
    isSubscriptionStep(state: CheckoutStoreState): boolean {
      return state.checkoutStep === CheckoutSteps.SELECT_PLAN_DETAILS;
    },
    isAioBundleFlow(state: CheckoutStoreState): boolean {
      return state.isAioBundle;
    },
    isCheckoutDetailsStep(state: CheckoutStoreState): boolean {
      return state.checkoutStep === CheckoutSteps.ADD_CHECKOUT_DETAILS;
    },
    isAddOnPurchaseStep(state: CheckoutStoreState): boolean {
      return state.checkoutStep === CheckoutSteps.SELECT_ADD_ON_DETAILS;
    },
    isSelectingPlanType(state: CheckoutStoreState): boolean {
      return (
        state.checkoutStep === CheckoutSteps.SELECT_PLAN_DETAILS ||
        state.checkoutStep === CheckoutSteps.ADD_CHECKOUT_DETAILS
      );
    },
    isChangePlanFlow(state: CheckoutStoreState): boolean {
      const onceStore = useOnceStore();

      return (
        onceStore.plan !== SUBSCRIPTION.PRO_TRIAL &&
        onceStore.plan !== SUBSCRIPTION.FREE &&
        onceStore.plan !== SUBSCRIPTION.MOBILE_ONLY &&
        onceStore.plan !== SUBSCRIPTION.FREEMIUM &&
        !this.isAppleToDeputyFlow &&
        state.checkoutStep === CheckoutSteps.SELECT_PLAN_DETAILS
      );
    },
    isTrialConvertFlow(): boolean {
      const onceStore = useOnceStore();
      return (
        onceStore.plan === SUBSCRIPTION.PRO_TRIAL ||
        onceStore.plan === SUBSCRIPTION.FREE ||
        onceStore.plan === SUBSCRIPTION.FREEMIUM ||
        this.isAppleToDeputyFlow
      );
    },
    isAppleToDeputyFlow(): boolean {
      const onceStore = useOnceStore();
      return (
        onceStore.channel === CHANNEL.APPSTORE_APPLE &&
        onceStore.getFeatureFlag('wo-2826-apple-to-deputy-self-serve')
      );
    },
    isAnnualToMonthlyAddOnFlow(): boolean {
      const addOnStore = useAddOnStore();
      return addOnStore.status.isMoveFromAnnualToMonthly;
    },
    isMonthlyToAnnualAddOnFlow(): boolean {
      const addOnStore = useAddOnStore();
      return addOnStore.status.isMoveFromMonthlyToAnnual;
    },
    isSwitchBillingScheduleFlow(): boolean {
      if (this.isAddOnPurchaseStep) {
        return false;
      }
      const relatedPlans = usePlansStore().getRelatedPlans(useCartStore().plan);
      return relatedPlans?.includes(useOnceStore().plan) || false;
    },
    isSwitchBillingScheduleToMonthlyFlow(): boolean {
      return (
        this.isSwitchBillingScheduleFlow &&
        usePlansStore().isMonthly(useCartStore().initialCartPlan)
      );
    },
    shouldHideAddOns(state: CheckoutStoreState): boolean {
      const onceStore = useOnceStore();
      return (
        !onceStore.isFreePlan &&
        onceStore.channel !== CHANNEL.APPSTORE_APPLE &&
        onceStore.channel !== CHANNEL.APPSTORE_GOOGLE &&
        state.checkoutStep === CheckoutSteps.SELECT_PLAN_DETAILS
      );
    },
  },
  actions: {
    async initializeCheckout(id: string): Promise<void> {
      await useOnceStore().loadBusiness(id);
      useCartStore().setInitialState();
    },
    setCheckoutStep(step: CheckoutSteps): void {
      this.checkoutStep = step;
    },
    incrementCheckoutStep(): void {
      if (this.checkoutStep < Object.keys(CheckoutSteps).length / 2 - 1) {
        this.checkoutStep++;
      }
    },
    decrementCheckoutStep(): void {
      if (this.checkoutStep > 0) {
        this.checkoutStep--;
      }
    },
    reset(): void {
      this.checkoutStep = CheckoutSteps.SELECT_PLAN_DETAILS;
      this.isAioBundle = false;
    },
  },
});
